import { createActionHelp, createFieldHelp } from '../ui/common/helpModel';
import { connectorBindingTooltips, workflowSetupTooltips } from './wizardTooltips';

export const mappingMenuSettingsTooltips = {
  workflowOption: createFieldHelp(
    'Workflow option',
    'Choose the PCF workflow that this saved mapping profile should follow.',
    {
      why: 'This keeps the mapping page aligned with the workflow chosen in the guided setup.',
      downstream: 'Changing it here affects which mapping sections and assumptions stay relevant.',
      inlineHint:
        'Keep this aligned with the workflow used in the wizard unless you intentionally want to reshape the profile.',
    }
  ),
  systemGrouping: workflowSetupTooltips.systemGrouping,
  objectType: workflowSetupTooltips.objectType,
  sKey: workflowSetupTooltips.sKey,
  pipingSpec: workflowSetupTooltips.pipingSpec,
} as const;

export const mappingGeneralAttributesTooltips = {
  objectType: workflowSetupTooltips.objectType,
  sKey: workflowSetupTooltips.sKey,
  pipingSpec: workflowSetupTooltips.pipingSpec,
  systemGrouping: workflowSetupTooltips.systemGrouping,
  insulationSpec: connectorBindingTooltips.insulationSpec,
  tracingSpec: connectorBindingTooltips.tracingSpec,
  insulationThickness: connectorBindingTooltips.insulationThickness,
  boltBaseFamily: createFieldHelp(
    'Bolt base family',
    'Choose whether PipeEX uses existing BOLT elements or lets BoltSetAdder derive standalone BOLT elements from qualifying GASKET elements.',
    {
      why: 'BoltBased does not run BoltSetAdder. GasketBased uses BOLT-SET-ACTIVE to identify qualifying GASKET elements that can produce standalone BOLT elements.',
      downstream: 'This selection changes where BOLT elements come from; it does not rename the BOLT PCF object or guarantee that a BOLT will be produced.',
    }
  ),
  weight: createFieldHelp(
    'Weight mapping',
    'Choose the parameter that carries component weight if BOM or downstream calculations need it.',
    {
      why: 'Weight is optional, but when it is mapped it should come from a maintained project value, not an inferred placeholder.',
      downstream: 'Mapped weight can feed BOM totals and downstream material review without manual cleanup.',
    }
  ),
} as const;

export const advancedSettingsTooltips = {
  centerOfCoordinates: createFieldHelp(
    'Coordinate origin',
    'Choose the Revit origin subtracted from coordinates written to the PCF file: the project base point for project-local coordinates or the survey point for survey-point-relative coordinates.',
    {
      why: 'Every exported component coordinate is measured from this Revit anchor.',
      downstream: 'The implementation translates XYZ values relative to the selected point and does not rotate them to True North.',
    }
  ),
} as const;

export const mappingSectionActionTooltips = {
  addAttribute: createActionHelp(
    'Add mapping attribute',
    'Add a custom PCF attribute to the current mapping section.',
    {
      prerequisite:
        'Use this for project-specific attributes that are not included in the default mappings.',
      downstream:
        'The new attribute is saved in the profile even if it was not configured in earlier wizard steps.',
    }
  ),
  deleteAttribute: createActionHelp(
    'Remove mapping attribute',
    'Remove this attribute from the active mapping section.',
    {
      prerequisite:
        'Use this when the attribute should not be included in the final PCF mapping.',
      downstream:
        'The attribute is removed only from the current mapping section. Settings configured in earlier wizard steps remain unchanged.',
    }
  ),
  formulaEditor: createActionHelp(
    'Edit formula mapping',
    'Open the script editor for a formula-based mapping row.',
    {
      downstream:
        'Use formula mode when the exported value must be derived instead of read directly from one Revit parameter.',
    }
  ),
} as const;

export const mappingTableTooltips = {
  selectVisibleRows: createActionHelp(
    'Select visible rows',
    'Toggle the selection of all rows currently visible after filters and view settings are applied.',
    {
      downstream:
        'Only rows in the current table view are affected. Hidden rows remain unchanged.',
    }
  ),
  rowOrder: createActionHelp(
    'Row order',
    'Sort rows by the order saved in the mapping profile.',
    {
      downstream:
        'The Order column lets you compare the current table order with the order stored in the profile.',
    }
  ),
  pcfAttributeColumn: createFieldHelp(
    'PCF attribute column',
    'Filter or sort by the PCF attribute name used in the exported file.',
    {
      why: 'This name defines the PCF attribute written during export, so it should be changed only when the profile requires different PCF output.',
      downstream:
        'Renaming or removing a PCF attribute changes the data available to downstream isometric and fabrication tools.',
    }
  ),
  revitParameterColumn: createFieldHelp(
    'Revit parameter column',
    'Choose or filter the Revit parameter that provides the value for each PCF attribute.',
    {
      why: 'This mapping connects Revit model data to the corresponding attributes in the exported PCF file.',
      downstream:
        'Selecting the wrong Revit parameter causes an incorrect value to be written to the PCF attribute, even if the attribute itself is mapped correctly.',
    }
  ),
  attributeTypeColumn: createFieldHelp(
    'Attribute type column',
    'Choose whether the PCF value comes from a Revit parameter, a BOM column, another attribute, or an IronPython formula.',
    {
      why: 'The attribute type determines how PipeEX obtains the value for the PCF attribute. It is independent of the object type selected in the navigator.',
      downstream:
        'Changing the attribute type changes how PipeEX determines the value written to the PCF attribute.',
    }
  ),
  formulaColumn: createFieldHelp(
    'Formula column',
    'Filter direct values or formulas, and open the editor for rows that calculate a PCF value using IronPython.',
    {
      why: 'Formula-based values should be reviewed carefully before export because they depend on the formula logic.',
      downstream:
        'The editor checks the formula syntax, but the formula is evaluated against Revit model data only during export.',
    }
  ),
  sourceColumn: createFieldHelp(
    'Source column',
    'Shows the workflow step or workspace area that originally seeded the row value.',
    {
      why: 'Source tells you whether a value came from setup, connectors, process properties, or a local workspace edit.',
      downstream:
        'Use this before changing a row so you know whether the upstream setup should be corrected instead.',
    }
  ),
  stateColumn: createFieldHelp(
    'State column',
    'Shows whether the row still follows its seeded value or has been changed locally.',
    {
      why: 'State separates normal seeded rows from local overrides that need review.',
      downstream:
        'Overridden rows are useful, but they should be intentional before saving a shared profile.',
    }
  ),
  actionsColumn: createActionHelp(
    'Row actions',
    'Use row actions to edit formulas or remove custom mapping rows.',
    {
      prerequisite:
        'Built-in required rows should usually be remapped or cleared rather than removed.',
    }
  ),
  requiredAttributeMarker: createFieldHelp(
    'Required mapping row',
    'This PCF attribute is required for the selected mapping scope.',
    {
      why: 'Required rows must have a parameter or formula before the profile is ready for validation and export.',
      downstream:
        'Leaving a required row unmapped creates validation work later in the workspace.',
    }
  ),
} as const;

export const mappingWorkspaceTooltips = {
  profileMenu: createActionHelp(
    'Open configuration profiles',
    'Switch between saved mapping profiles for the current scope and load them into the workspace.',
    {
      downstream:
        'Loading a profile replaces the active workspace state with the saved configuration from that profile.',
    }
  ),
  companyScope: createActionHelp(
    'Company profile scope',
    'Show profiles intended to be shared across a wider team or company workflow.',
    {
      downstream:
        'Use this when the mapping should start from a common baseline instead of a personal draft.',
    }
  ),
  userScope: createActionHelp(
    'User profile scope',
    'Show personal mapping profiles that belong to the current user workflow.',
    {
      downstream:
        'Use this for in-progress or user-specific profile variations before they are promoted to a shared baseline.',
    }
  ),
  refreshProfiles: createActionHelp(
    'Refresh profiles',
    'Reload the available saved profiles for the current scope.',
    {
      downstream:
        'Use this after saving or deleting profiles so the menu reflects the latest state.',
    }
  ),
  saveProfile: createActionHelp(
    'Save selected profile',
    'Overwrite the currently selected saved profile with the current mapping workspace state.',
    {
      downstream:
        'Use this when you want to keep the same profile name and scope but update its saved mapping, Quick Fill, and workflow choices.',
    }
  ),
  saveProfileAs: createActionHelp(
    'Save current workspace as a profile',
    'Create a new saved profile from the current mapping workspace state.',
    {
      downstream:
        'This preserves the current mapping, Quick Fill, and related workflow choices as a reusable starting point.',
    }
  ),
  deleteProfile: createActionHelp(
    'Delete profiles',
    'Select one or more saved profiles and remove them together.',
    {
      prerequisite:
        'At least one mapping profile must remain after deletion.',
      downstream:
        'The selected User and Company profiles are deleted after one confirmation, then the profile list refreshes.',
    }
  ),
  loadProfile: createActionHelp(
    'Load selected profile',
    'Apply the selected saved profile to the current mapping workspace.',
    {
      downstream:
        'This updates the workspace to match the saved configuration, including earlier wizard-derived mapping values.',
    }
  ),
  mappingTab: createActionHelp(
    'Open mapping workspace',
    'Review PCF attribute rows by object type and connect each export field to a Revit parameter or expression.',
    {
      downstream:
        'This is where the final saved profile decides exactly what the PCF export emits.',
    }
  ),
  quickFillTab: createActionHelp(
    'Open Quick Fill workspace',
    'Review families in bulk, fill missing values, and normalize parameter coverage before final save.',
    {
      downstream:
        'Cleaner family data here reduces unresolved issues in validation and export review.',
    }
  ),
  validationTab: createActionHelp(
    'Open validation workspace',
    'Inspect unresolved validation issues and jump back to the step most likely to fix them.',
    {
      downstream:
        'Use this as the final quality gate before exporting PCF files.',
    }
  ),
  exportTab: createActionHelp(
    'Open export workspace',
    'Review detected piping systems, check their status, and export the PCF output when the workspace is ready.',
    {
      downstream:
        'This is the final export handoff surface after mapping and validation are in place.',
    }
  ),
  applyChanges: createActionHelp(
    'Apply workspace changes',
    'Save the current workspace edits without leaving the Mapping workspace.',
    {
      downstream:
        'Use this when you want to keep working after preserving the latest changes.',
    }
  ),
  saveAndClose: createActionHelp(
    'Save and close workspace',
    'Save the current workspace state and finish the Mapping step.',
    {
      downstream:
        'Use this when mapping, validation, and export preparation are complete for this session.',
    }
  ),
  currentScopeRail: createActionHelp(
    'Mapping scope',
    'Use this navigator to switch between shared engineering mappings and mappings for specific PCF object types. The active selection shows which mapping set you are editing.',
    {
      downstream:
        'ALL defines the default mappings for all object types. Select a specific object type to review or override its PCF mappings.',
    }
  ),
  focusedLens: createActionHelp(
    'Focused rows lens',
    'Show only the rows currently highlighted by validation or issue navigation.',
    {
      downstream:
        'This helps you work only the rows tied to the active validation issue instead of scanning the whole table.',
    }
  ),
  unmappedLens: createActionHelp(
    'Unmapped required lens',
    'Show only required PCF attributes that are still missing a mapping.',
    {
      downstream:
        'Use this to clear required gaps quickly before validation or export.',
    }
  ),
  overriddenLens: createActionHelp(
    'Overridden rows lens',
    'Show only rows whose current value no longer matches the upstream seeded value.',
    {
      downstream:
        'This helps you audit local overrides separately from rows that still follow workflow defaults.',
    }
  ),
} as const;

export const mappingWorkspaceChromeTooltips = {
  activePageGuide: createActionHelp(
    'Open active workspace guide',
    'Open the written guide for the Mapping, Quick Fill, Validation, or Export workspace that is currently active.',
    {
      downstream:
        'Use this when the active tab needs workflow context without leaving the integrated mapping workspace.',
    }
  ),
  activeWorkspaceTour: createActionHelp(
    'Start active workspace tour',
    'Replay the spotlight tour for the current workspace tab.',
    {
      downstream:
        'The tour follows the active tab, so Mapping, Quick Fill, Validation, and Export each focus their own controls.',
    }
  ),
  setupSummary: createActionHelp(
    'Review setup summary',
    'Open the workflow summary carried forward from the setup wizard into the mapping workspace.',
    {
      downstream:
        'Use this to compare table values against upstream setup choices before changing saved mappings.',
    }
  ),
  profileDetails: createActionHelp(
    'Profile details',
    'Show metadata and workflow cross-checks for the selected saved profile.',
    {
      downstream:
        'Use this before loading or saving when you need to confirm which workflow choices the profile represents.',
    }
  ),
  signOut: createActionHelp(
    'Sign out',
    'End the current user session from the workspace chrome.',
    {
      downstream:
        'Unsaved workspace edits should be applied before signing out so profile changes are not left only in memory.',
    }
  ),
  closeWorkspace: createActionHelp(
    'Close workspace window',
    'Close the integrated mapping workspace window.',
    {
      downstream:
        'Use Apply or Save and close first when the current mapping edits should be persisted.',
    }
  ),
} as const;

export const mappingOverviewTooltips = {
  currentSection: createActionHelp(
    'Current mapping section',
    'Shows which mapping section is currently active in the main content area.',
    {
      downstream:
        'Use it as a quick orientation cue when moving between object types, process sections, and status tools.',
    }
  ),
  configurationOverview: createActionHelp(
    'Configuration overview',
    'Review the values carried forward from each earlier workflow step before you edit the final mapping.',
    {
      downstream:
        'Use this as a checkpoint when the final mapping looks wrong and you need to trace the source step.',
    }
  ),
  overviewSectionCard: createActionHelp(
    'Open workflow section',
    'Jump directly to the selected workflow section to edit the values shown in this summary card.',
    {
      downstream:
        'This lets you correct upstream settings without hunting through the full workspace tree.',
    }
  ),
  statusBuilder: createActionHelp(
    'Status builder',
    'Define engineering status categories and the Revit project phase dimension used to assign matching elements.',
    {
      downstream:
        'Each category can map status-related PCF attributes from constants or Revit parameters for elements created or demolished in a phase.',
    }
  ),
  addStatusCategory: createActionHelp(
    'Add status category',
    'Create a new reusable status category in the status builder.',
    {
      downstream:
        'Use this when the project needs a status bucket that is not covered by the built-in set.',
    }
  ),
  deleteStatusCategory: createActionHelp(
    'Delete status category',
    'Remove a custom status category from the builder.',
    {
      downstream:
        'Built-in categories stay protected, but custom categories can be removed when they are no longer needed.',
    }
  ),
  deleteStatusCategoryDisabled: createActionHelp(
    'Built-in status category',
    'Built-in status categories cannot be deleted from the status builder.',
    {
      why: 'PipeEX depends on these categories as stable groups for status-related mapping logic.',
      downstream:
        'Create a custom category when the project needs a removable status bucket.',
    }
  ),
  statusCategoryMapping: createActionHelp(
    'Status category mapping',
    'Choose whether each status-related PCF attribute receives a constant or a Revit parameter value for this category.',
    {
      downstream:
        'The selected Revit phase property determines which elements use these category-specific status values.',
    }
  ),
  addStatusAttribute: createActionHelp(
    'Add status attribute',
    'Add another PCF-to-Revit mapping row inside the selected status category.',
    {
      downstream:
        'This lets a status category carry extra status-related metadata into export.',
    }
  ),
  selectionTreeAddObjectType: createActionHelp(
    'Add object type',
    'Add a PCF object type that requires mappings different from the default ALL scope.',
    {
      downstream:
        'The new object-type scope keeps the same PCF object type ID while allowing its Revit-to-PCF mappings to override the ALL defaults.',
    }
  ),
  selectionTreeDeleteObjectType: createActionHelp(
    'Delete object type',
    'Remove the selected object-type mapping from the workspace.',
    {
      downstream:
        'The object-specific mapping is removed from the saved profile, and the object type will use the default ALL mappings.',
    }
  ),
  workflowCompletion: createActionHelp(
    'Setup summary',
    'Review the settings, parameters, and mappings configured during setup before continuing to Mapping, Quick Fill, Validation, or Export.',
    {
      downstream:
        'The summary shows what has already been configured and helps you identify the appropriate next workflow.',
    }
  ),
  workspaceGuide: createActionHelp(
    'Workspace guide',
    'See what each workspace is used for and which one is recommended for the next step.',
  ),
} as const;
